<template>
  <div>
    <BaseModal
      id="ModalUploadProfile"
      ref="modal__upload-profile"
      title="Profile Picture"
      class="modal__upload-profile"
      size="lg"
      :show.sync="$show"
      :hide-footer="!isChange"
      @closeModal="clearData"
    >
      <div v-if="!newFile && !isChange" class="container__diaplay-profile">
        <!-- <base-presigned-image
          v-if="$file && $file[0]"
          class="rounded-circle overflow-hidden container__diaplay-profile-img"
          :file-id="$file[0].id"
        ></base-presigned-image> -->

        <div v-if="$file && $file[0]">
          <b-skeleton v-if="isFetching" type="avatar" size="300px"></b-skeleton>

          <img
            v-else
            :src="profileImageSrc"
            class="rounded-circle overflow-hidden container__diaplay-profile-img"
          />
        </div>
        <BaseAvatar v-else :name="`${user.firstname} ${user.lastname}`" />
      </div>

      <div class="container__cropper">
        <CardCroppper ref="cardCropper" :src="image.src"></CardCroppper>

        <p v-if="!isChange" class="text__upload-description">
          (Supported File Types: .jpg, .jpeg, .png)
        </p>

        <UploadProfileButtonGroup
          v-if="!isChange"
          class="button-wrapper"
          :hide-edit="$originalFile && $originalFile.length ? false : true"
          :hide-delete="$originalFile && $originalFile.length ? false : true"
          @onEdit="onEdit"
          @onUpload="uploadImage"
          @onDelete="onDelete"
        ></UploadProfileButtonGroup>
      </div>

      <template #modal-footer>
        <div>
          <b-button
            v-if="isChange"
            variant="link"
            class="btn-link-dark"
            type="reset"
            :disabled="isUploading"
            @click="clearData"
          >
            Cancel
          </b-button>
          <b-button
            v-if="isChange"
            variant="success"
            class="button"
            type="submit"
            :disabled="isUploading"
            @click="onSubmit"
          >
            <span v-if="!isUploading">Confirm</span>
            <span v-else class="pr-2 pl-2">
              <b-spinner label="Loading..." variant="secondary" small /> </span
          ></b-button>
        </div>
      </template>
    </BaseModal>

    <base-s3-uploader
      v-show="false"
      id="input-media"
      ref="profileUploader"
      :reference-meta="{ ...referenceMeta, field: 'profile_picture', source: 'users-permissions' }"
      :dropzone-options="dropzoneOptions"
      :is-public="true"
    >
    </base-s3-uploader>

    <base-s3-uploader
      v-show="false"
      id="input-media"
      ref="originalProfileUploader"
      :reference-meta="{
        ...referenceMeta,
        field: 'original_profile_picture',
        source: 'users-permissions'
      }"
      :dropzone-options="dropzoneOptions"
      :is-public="true"
    >
    </base-s3-uploader>

    <ModalConfirmDelete
      :show.sync="activeModalDelete"
      :is-loading="isUploading"
      @confirmed:delete="deleteImage"
    />
  </div>
</template>

<script>
import 'vue-advanced-cropper/dist/style.css';
import { BaseModal } from '@/components/base-components';
import BaseAvatar from '@/components/base-components/BaseAvatar.vue';
import BaseS3Uploader from '@/components/base-components/BaseS3Uploader';
// import BasePresignedImage from '@/components/base-components/BasePresignedImage.vue';
import ModalConfirmDelete from './ModalConfirmDelete.vue';
import UploadProfileButtonGroup from './UploadProfileButtonGroup.vue';
import User from '@/models/User';
import Upload from '@/models/Upload';
import CardCroppper from './CardCroppper.vue';

export default {
  name: 'ModalUploadProfile',
  components: {
    BaseModal,
    BaseAvatar,
    BaseS3Uploader,
    // BasePresignedImage,
    ModalConfirmDelete,
    UploadProfileButtonGroup,
    CardCroppper
  },
  props: {
    user: {
      type: Object,
      default: () => ({})
    },
    show: {
      type: Boolean,
      default: false
    },

    referenceMeta: {
      type: Object,
      default: () => ({})
    },
    file: {
      type: Array,
      default: () => []
    },
    originalFile: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      image: {
        src: null,
        type: 'image/jpg',
        name: null
      },
      profileImageSrc: null,
      newFile: null,
      dropzoneOptions: {
        type: Object,
        default: () => ({
          acceptedFiles: '.jpeg, .jpg, .png',
          maxFiles: 1,
          maxfilesexceeded: function (file) {
            this.removeAllFiles(file);
            this.addFile(file);
          },
          maxFilesize: 2 //MB
        })
      },
      isUploading: false,
      isFetching: false,
      activeModalDelete: false,
      isChange: false
    };
  },
  computed: {
    $show: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      }
    },
    $file: {
      get() {
        return this.file;
      },

      set(v) {
        this.$emit('update:file', v);
      }
    },
    $originalFile: {
      get() {
        return this.originalFile;
      },

      set(v) {
        this.$emit('update:originalFile', v);
      }
    }
  },

  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value) {
          this.fetchProfileImageSrc();
        }
      }
    }
  },

  methods: {
    async onSubmit() {
      this.isUploading = true;
      try {
        const result = this.$refs.cardCropper.$refs.cropper.getResult();

        if (this.file && this.file.length) {
          this.$refs.profileUploader.$refs.vueDropzone.removeAllFiles(this.file[0]);
        }
        if (this.originalFile && this.originalFile.length && !this.isEdit) {
          this.$refs.originalProfileUploader.$refs.vueDropzone.removeAllFiles(this.originalFile[0]);
        }
        if (this.$refs.profileUploader.hasAnyDeletedFiles() && !this.isEdit) {
          await this.$refs.profileUploader.deleteRemovedFiles();
        }
        if (this.$refs.originalProfileUploader.hasAnyDeletedFiles()) {
          await this.$refs.originalProfileUploader.deleteRemovedFiles();
        }

        let resultOriginal = null;
        if (this.newFile) {
          await this.$refs.originalProfileUploader.addFile(this.newFile[0]);
          if (this.$refs.originalProfileUploader.hasAnyQueuedFiles()) {
            resultOriginal = await this.$refs.originalProfileUploader.upload();
            this.$originalFile = [this.newFile[0]];
          }
        }

        result.canvas.toBlob(async (blob) => {
          // Do something with blob: upload to a server, download and etc.
          const file = new File([blob], this.image.name, { type: this.image.type });

          //upload profile_picture
          await this.$refs.profileUploader.addFile(file);

          let result = null;
          if (this.$refs.profileUploader.hasAnyQueuedFiles()) {
            result = await this.$refs.profileUploader.upload();
          }

          this.$emit('upload:success', {
            file: result[0],
            originalFile: resultOriginal ? resultOriginal[0] : this.$originalFile[0]
          });
        }, this.image.type);

        this.$emit('update:show', false);
      } catch (error) {
        console.error(error);
      } finally {
        this.isChange = false;
        this.isUploading = false;
      }
    },

    uploadImage(file) {
      this.isChange = true;
      // Ensure that you have a file before attempting to read it
      if (file) {
        // 1. Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
        if (this.image.src) {
          URL.revokeObjectURL(this.image.src);
        }
        // 2. Create the blob link to the file to optimize performance:
        const blob = URL.createObjectURL(file);

        // 3. Update the image. The type will be derived from the extension and it can lead to an incorrect result:
        this.image = {
          src: blob,
          type: file.type,
          name: file.name
        };

        this.newFile = [file];
      }
    },

    clearData() {
      this.isChange = false;
      this.newFile = null;

      if (this.image && this.image.src) {
        this.image.src = null;
        this.image.type = null;
        this.image.name = null;
      }
    },

    onEdit() {
      this.isChange = true;

      this.image.src = this.profileImageSrc ?? this.originalFile[0]?.dataURL;
    },

    onDelete() {
      this.activeModalDelete = true;
    },

    async deleteImage() {
      this.activeModalDelete = false;
      this.isUploading = true;
      try {
        if (this.file && this.file.length) {
          this.$refs.profileUploader.deletedIds.push(this.$file[0].id);
          this.$refs.profileUploader.$refs.vueDropzone.removeAllFiles(this.$file);
        } else {
          this.$refs.profileUploader.$refs.vueDropzone.removeAllFiles();
        }
        if (this.$refs.profileUploader.hasAnyDeletedFiles()) {
          await this.$refs.profileUploader.deleteRemovedFiles();
        }

        //originalProfileUploader
        if (this.originalFile && this.originalFile.length) {
          this.$refs.originalProfileUploader.deletedIds.push(this.$originalFile[0].id);
          this.$refs.originalProfileUploader.$refs.vueDropzone.removeAllFiles(this.originalFile[0]);
        } else {
          this.$refs.originalProfileUploader.$refs.vueDropzone.removeAllFiles();
        }
        if (this.$refs.originalProfileUploader.hasAnyDeletedFiles()) {
          await this.$refs.originalProfileUploader.deleteRemovedFiles();
        }
        this.image.src = null;
        this.image.type = null;
        this.image.name = null;
        this.profileImageSrc = null;

        this.$emit('update:file', []);
        this.$emit('update:originalFile', []);

        await User.update({
          where: this.user.id,
          data: {
            profile_picture: null,
            original_profile_picture: null
          }
        });

        this.$emit('deleted:success');
        this.activeModalDelete = false;
      } catch (error) {
        console.log(error);
      } finally {
        this.isUploading = false;
      }
    },

    async fetchProfileImageSrc() {
      if (this.file && this.file.length) {
        this.isFetching = true;

        const { response } = await Upload.api().fetchPresignedUrl(this.file[0].id);

        this.profileImageSrc = response.data.url;

        this.isFetching = false;
      }
    }
  }
};
</script>

<style lang="scss">
.modal__upload-profile {
  .modal-body {
    padding: 0;
    .container__diaplay-profile {
      background: #f4f5fd;
      padding: 1rem;
    }
    .container__cropper {
      padding: 1rem;
    }
  }
  .modal-content {
    .modal-header {
      h5 {
        font-size: 20px;
      }
    }
  }
  .text__upload-description {
    display: flex;
    justify-content: center;
    font-size: 16px;
    color: #3b3e66;
  }
  .cropper {
    max-height: 500px;
  }

  .button-wrapper {
    display: flex;
    justify-content: center;
  }

  .button {
    cursor: pointer;
    input {
      display: none;
    }
  }
  .container__diaplay-profile {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .container__diaplay-profile-img {
    max-height: 400px;
    min-height: 300px;
    height: 100%;
  }

  @media only screen and (max-width: 346px) {
    .container__diaplay-profile-img {
      max-height: 200px;
      min-height: 100px;
      height: 100%;
    }
  }
  // @media only screen and (min-width: 347px) and (max-width: 767px) {
  //   .container__diaplay-profile-img {
  //     max-height: 300px;
  //     height: 100%;
  //   }
  // }
}
</style>
